


































import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator'
import request, { Response } from '@/helpers/requests'
import AccountIdInput from '@/components/ui/AccountIdInput.vue'
import BaseTechTaskCard from './BaseTechTaskCard.vue'
import { CardState } from '@/dynamic-tech-task/types'
import debug from '@/helpers/debug'
import DisplayFields from '@/components/ui/DynamicTechTaskCard/DisplayFields.vue'
import DynamicForm from '@/components/ui/DynamicTechTaskCard/DynamicForm.vue'
import { InputType } from '@/components/ui/DynamicTechTaskCard/types'
import { OnSubmitEvent } from '@/components/ui/types'
import TechTaskCard from '@/components/ui/TechTaskCard.vue'


interface SubmitResponse extends Response {
    message: string;
    data: {
        inputData: { [key: string]: unknown };
        displayData: { [key: string]: unknown }|null;
    };
}


interface TechTaskCardType extends Vue {
    submit(): Promise<OnSubmitEvent<SubmitResponse>>;
}


@Component({
    components: {
        AccountIdInput,
        BaseTechTaskCard,
        DynamicForm,
        DisplayFields,
        TechTaskCard
    }
})
export default class DynamicTechTaskCard<S extends SubmitResponse> extends Vue {
    @Prop({ required: true }) taskName !: string
    @Prop({ required: true }) title !: string
    locationValid = false
    showSuccess = false
    $refs!: {
        techTaskCard: TechTaskCardType;
    }

    get currentState(): CardState {
        return this.$store.state.techTask.cardState
    }

    get hideActions(): boolean {
        for (const inputField of this.$store.getters.inputFields) {
            if (inputField.inputType === InputType.ORDERED_LIST) {
                return true
            }
        }
        return false
    }

    get requestPayload(): { [key: string]: unknown } {
        return this.$store.state.techTask.inputData
    }

    get formLoading(): boolean {
        return this.currentState === CardState.FORM_LOADING
    }

    get formError(): boolean {
        return this.currentState === CardState.FORM_ERROR
    }

    get formReady(): boolean {
        return this.currentState === CardState.READY
    }

    get loading(): boolean {
        return this.currentState === CardState.LOADING
    }

    async mounted() {
        await this.$store.dispatch('loadForm', this.taskName)
    }

    /**
     * Router does not refresh the component, it only updates the properties. Since instances are not being recreated
     * on navigation, it is up to us to reset the state.
     */
    @Watch('taskName')
    reload() {
        this.locationValid = false
        this.$store.dispatch('loadForm', this.taskName)
    }

    /**
     * When a location is updated we reset the display data and all of the input fields that are not pulseM account ID.
     */
    onLocationChange(locationValid: boolean) {
        debug.info(`Location changed: ${locationValid}`)
        this.locationValid = locationValid
        this.$store.dispatch('setDisplayData', null)

        // Reset input field values.
        if (locationValid && this.$store.state.techTask.form) {
            this.$store.dispatch('resetInputFields')
            this.$store.dispatch('formLoadData')
        }
    }

    @Emit('on-submit')
    async submit() {
        await this.$store.dispatch('submitForm', this.requestPayload)
        try {
            this.$store.commit('setCardState', {
                cardState: CardState.LOADING,
                message: null
            })
            const response: S = await request.post(`/support/${this.taskName}/submit`, {
                ...this.requestPayload
            }) as S
            this.$store.commit('setCardState', {
                cardState: CardState.READY,
                message: null
            })
            this.showSuccess = true
            await this.onSubmit(response)
        } catch(e) {
            this.$store.commit('setCardState', {
                cardState: CardState.ERROR,
                message: e.message
            })
            await this.onSubmit(null)
        }
    }

    /**
     * This is executed upon successful execution of a tech task.
     */
    async onSubmit(response: S|null) {
        // If the backend-end provides us with inputData, we can trust that this is the most up to date or correct data.
        if (response?.data.inputData) {
            for (const inputField of this.$store.getters.inputFields) {
                if (inputField.inputType !== InputType.PULSEM_ACCOUNT_ID) {
                    await this.$store.dispatch('setInputData', {
                        key: inputField.key,
                        value: response?.data.inputData[inputField.key]
                    })
                }
            }
        }
        // Some backend-ends have display fields enabled which allow us to dynamically display information to users.
        await this.$store.dispatch('setDisplayData', response?.data.displayData || null)
    }
}
